<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { GridStack } from 'gridstack'
import 'gridstack/dist/gridstack.min.css'
import { useDashboardStore } from '@/stores/dashboard'
import WidgetContainer from './WidgetContainer.vue'

const props = defineProps({
  widgets: {
    type: Array,
    required: true,
    default: () => []
  },
  isLocked: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['configure', 'delete', 'refresh'])

const dashboardStore = useDashboardStore()

// Refs
const gridRef = ref(null)
let gridInstance = null
let debounceTimer = null

// Initialize GridStack
onMounted(() => {
  initGrid()
})

// Cleanup on unmount
onUnmounted(() => {
  destroyGrid()
})

// Watch for widget changes
watch(() => props.widgets, async (newWidgets, oldWidgets) => {
  if (!gridInstance) return

  // Wait for DOM update
  await nextTick()

  // Check if widgets were added or removed
  const oldIds = new Set(oldWidgets?.map(w => w.id) || [])
  const newIds = new Set(newWidgets.map(w => w.id))

  // Find added widgets
  const addedWidgets = newWidgets.filter(w => !oldIds.has(w.id))

  // Find removed widgets
  const removedIds = [...oldIds].filter(id => !newIds.has(id))

  // Remove widgets that no longer exist
  removedIds.forEach(id => {
    const el = gridRef.value?.querySelector(`[gs-id="${id}"]`)
    if (el) {
      gridInstance.removeWidget(el, false)
    }
  })

  // Add new widgets - they will be rendered by Vue and picked up by GridStack
  if (addedWidgets.length > 0) {
    await nextTick()
    addedWidgets.forEach(widget => {
      const el = gridRef.value?.querySelector(`[gs-id="${widget.id}"]`)
      if (el) {
        gridInstance.makeWidget(el)
      }
    })
  }
}, { deep: true })

// Watch for lock state changes
watch(() => props.isLocked, (locked) => {
  if (gridInstance) {
    gridInstance.setStatic(locked) // Set static mode based on lock state
  }
})

// Initialize the GridStack grid
function initGrid() {
  if (!gridRef.value) return

  gridInstance = GridStack.init({
    column: 12,
    cellHeight: 80,
    margin: 10,
    float: true,
    animate: true,
    staticGrid: props.isLocked, // Set initial static state
    draggable: {
      handle: '.widget-header'
    },
    resizable: {
      handles: 'e, se, s, sw, w'
    }
  }, gridRef.value)

  // Listen for change events
  gridInstance.on('change', handleGridChange)

  // Make existing widgets
  props.widgets.forEach(widget => {
    const el = gridRef.value?.querySelector(`[gs-id="${widget.id}"]`)
    if (el) {
      gridInstance.makeWidget(el)
    }
  })
}

// Destroy the GridStack grid
function destroyGrid() {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }

  if (gridInstance) {
    gridInstance.off('change')
    gridInstance.destroy(false)
    gridInstance = null
  }
}

// Handle grid change events with debounce
function handleGridChange(event, items) {
  if (!items || items.length === 0) return

  // Clear existing timer
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }

  // Debounce the layout update
  debounceTimer = setTimeout(() => {
    const layouts = items.map(item => ({
      widget_id: item.id,
      layout: {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      }
    }))

    dashboardStore.updateLayouts(layouts).catch(err => {
      console.error('Failed to update layouts:', err)
    })
  }, 300)
}

// Get grid attributes for a widget
function getGridAttrs(widget) {
  const layout = widget.layout || {}
  return {
    'gs-id': widget.id,
    'gs-x': layout.x ?? 0,
    'gs-y': layout.y ?? 0,
    'gs-w': layout.w ?? 4,
    'gs-h': layout.h ?? 3,
    'gs-min-w': 2,
    'gs-min-h': 2
  }
}

// Forward events from WidgetContainer
function handleConfigure(widget) {
  emit('configure', widget)
}

function handleDelete(widgetId) {
  emit('delete', widgetId)
}

function handleRefresh(widgetId) {
  emit('refresh', widgetId)
}
</script>

<template>
  <div class="dashboard-grid-wrapper">
    <div ref="gridRef" class="grid-stack">
      <div
        v-for="widget in widgets"
        :key="widget.id"
        class="grid-stack-item"
        v-bind="getGridAttrs(widget)"
      >
        <div
          class="grid-stack-item-content"
          :class="{ 'transparent-container': isLocked && widget.config?.transparentContainer === true }"
        >
          <WidgetContainer
            :widget="widget"
            :is-locked="isLocked"
            @configure="handleConfigure(widget)"
            @delete="handleDelete(widget.id)"
            @refresh="handleRefresh(widget.id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard-grid-wrapper {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.grid-stack {
  background: transparent;
  min-height: 100%;
  margin: -10px;
}

/* GridStack item styling */
.grid-stack-item {
  background: transparent;
}

.grid-stack-item-content {
  background: var(--surface-card);
  border: none;
  border-radius: var(--radius-xl);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Transparent container styling (only when locked) */
.grid-stack-item-content.transparent-container {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* Maintain visual feedback during drag even if transparent */
:deep(.grid-stack-item.ui-draggable-dragging .grid-stack-item-content.transparent-container) {
  /* Temporarily show outline during drag for visual feedback */
  outline: 2px dashed rgba(115, 87, 210, 0.3);
  outline-offset: -2px;
}

/* GridStack placeholder styling */
:deep(.grid-stack-placeholder) {
  background: var(--accent-soft-2) !important;
  border: 2px dashed rgba(115, 87, 210, 0.5) !important;
  border-radius: var(--radius-lg);
}

/* Resize handle styling */
:deep(.ui-resizable-handle) {
  background: transparent;
}

:deep(.ui-resizable-se) {
  width: 20px;
  height: 20px;
  bottom: 0;
  right: 0;
  cursor: se-resize;
}

:deep(.ui-resizable-se::after) {
  content: '';
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 12px;
  height: 12px;
  border-right: 2px solid rgba(255, 255, 255, 0.2);
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 0 0 4px 0;
}

:deep(.grid-stack-item:hover .ui-resizable-se::after) {
  border-color: rgba(115, 87, 210, 0.5);
}

/* Dragging state */
:deep(.grid-stack-item.ui-draggable-dragging) {
  opacity: 0.9;
  z-index: 100;
}

:deep(.grid-stack-item.ui-draggable-dragging .grid-stack-item-content) {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border-color: rgba(115, 87, 210, 0.5);
}

/* Scrollbar styling */
.dashboard-grid-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.dashboard-grid-wrapper::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: var(--radius-sm);
}

.dashboard-grid-wrapper::-webkit-scrollbar-thumb {
  background: var(--border-strong);
  border-radius: var(--radius-sm);
}

.dashboard-grid-wrapper::-webkit-scrollbar-thumb:hover {
  background: var(--border-strong);
}
</style>
